import React from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardHeader,
  Chip,
  Divider,
  Grid,
  Icon,
  IconButton,
  Rating,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import RatingModal from "pages/Home/Information/RatingModal";

function ActivityCard({
  variant,
  color,
  icon,
  idActivity,
  title,
  address,
  description,
  bies,
  date,
  rating,
  route,
  isOngoing,
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const [ratingStars, setRatingStars] = React.useState(rating);

  React.useEffect(() => {
    setRatingStars(rating);
  }, [rating]);

  const handleNavigate = () => {
    navigate(route);
  };

  const ribbonStyle = {
    position: "absolute",
    top: 16,
    left: -16,
    backgroundColor: theme.palette.warning.main,
    color: "#fff",
    padding: "4px 30px",
    transform: "rotate(-30deg)",
    fontWeight: 600,
    fontSize: "0.75rem",
    boxShadow: theme.shadows[2],
    zIndex: 2,
    borderRadius: 4,
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    "&:hover": {
      transform: "scale(1.02)",
      boxShadow: theme.shadows[6],
    },
  };

  return (
    <Grid item xs={12} md={6}>
      <Box position="relative">
        {isOngoing && <Box sx={ribbonStyle}>Ongoing</Box>}

        <Card
          elevation={4}
          sx={{
            borderRadius: 4,
            backdropFilter: "blur(6px)",
            background: "rgba(255, 255, 255, 0.85)",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
            "&:hover": {
              transform: "scale(1.02)",
              boxShadow: theme.shadows[6],
            },
          }}
        >
          <CardActionArea
            onClick={handleNavigate}
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {!title ? (
              <>
                <Skeleton variant="text" width="70%" height={32} />
                <Skeleton variant="text" width="40%" />
              </>
            ) : (
              <CardHeader
                title={
                  <Typography variant="h5" fontWeight="bold">
                    {title}
                  </Typography>
                }
                action={
                  <IconButton>
                    <Icon>arrow_forward</Icon>
                  </IconButton>
                }
              />
            )}

            <Divider sx={{ my: 1 }} />

            <Stack spacing={2}>
              {/* Address */}
              <InfoChip icon="place" label={address} loading={!address} />

              {/* Description */}
              <InfoChip icon="home_repair_service" label={description} loading={!description} />

              {/* Bies */}
              <InfoChip
                icon="person"
                label={Array.isArray(bies) && bies.length ? bies.join(", ") : "-"}
                loading={!bies}
              />

              {/* Rating */}
              {ratingStars && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" color="text.secondary">
                    User Rating:
                  </Typography>
                  <Rating value={parseFloat(ratingStars)} precision={0.5} size="small" readOnly />
                </Box>
              )}
            </Stack>
          </CardActionArea>

          {/* Show rating modal if not rated yet */}
          {!ratingStars && (
            <CardActions sx={{ justifyContent: "center", pb: 2 }}>
              <RatingModal
                idActivity={idActivity}
                title={title ?? ""}
                description={description ?? ""}
                date={date ?? ""}
                bies={bies ?? []}
                callback={setRatingStars}
              />
            </CardActions>
          )}
        </Card>
      </Box>
    </Grid>
  );
}

// Reusable InfoChip Component
const InfoChip = ({ icon, label, loading }) => {
  return loading ? (
    <Skeleton variant="text" width="80%" />
  ) : (
    <Chip
      icon={<Icon>{icon}</Icon>}
      label={
        <Box
          component="span"
          sx={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            display: "block", // forces it to be treated as a block
            textAlign: "left",
            lineHeight: 1.2,
          }}
        >
          {label}
        </Box>
      }
      variant="outlined"
      sx={{
        fontSize: 14,
        py: 1,
        px: 2,
        borderRadius: 2,
        maxWidth: "100%", // optional, limits chip width
        alignItems: "flex-start", // ensures proper alignment if multiline
        height: "auto", // important: allows the chip to expand vertically
      }}
    />
  );
};

ActivityCard.defaultProps = {
  variant: "contained",
  color: "info",
  bies: [],
  isOngoing: false,
};

ActivityCard.propTypes = {
  variant: PropTypes.oneOf(["contained", "gradient"]),
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
  ]),
  icon: PropTypes.node.isRequired,
  idActivity: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  address: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  rating: PropTypes.string.isRequired,
  bies: PropTypes.array,
  route: PropTypes.string.isRequired,
  isOngoing: PropTypes.bool,
};

export default ActivityCard;
